/**
 * The profile as a plain value: its type, what a person with no row yet
 * looks like, and the small guarded reads every screen would otherwise
 * restate.
 *
 * Split from `profile.ts` (the hook) for the reason `reach.ts` and
 * `entries.ts`' neighbours are: the root suite loads these files directly
 * with `node --test`, and a file that imports TanStack Query and Supabase
 * cannot be loaded there. The one import below is a type, which the loader
 * erases. `profile.ts` re-exports all of it.
 *
 * ## Why the defaults are these
 *
 * `DEFAULT_PROFILE` is `20261005130000_profiles.sql`'s column defaults,
 * hand-copied (`test/profile-defaults.test.ts` asserts each, so a schema
 * change breaks a test rather than drifting): English-only on, English talks,
 * 2.5 km, Berlin's own airport, Ask on, model scoring off. Home is null and
 * the name is null — a person the app knows nothing about has no "near you",
 * no lines and no second home, and nothing here guesses otherwise. That is
 * the point of the file: these were one person's facts written as constants,
 * and the neutral value for everybody else is *absent*, not Doug's.
 *
 * Doug's own row holds today's constants, so once his profile has loaded
 * nothing he sees moves. Until it has (first load, no cache), he sees the
 * defaults — English-only and English talks, which are his values too.
 */
import type { Database } from './database.types'

export type Profile = Database['public']['Tables']['profiles']['Row']

/** `profiles.second_home`: where else this person lives. `airports` is the
 *  list of codes that count as "going home" — first is the default. */
export type SecondHome = { label: string; country: string; airports: string[] }

/** The columns a person can write. `ask_enabled` and `model_scoring` are the
 *  admin's, and `calendar_synced_at` / `calendar_sync_note` are the Worker's
 *  (the migrations' column grants leave them out), so a patch that carried one
 *  would be refused with 42501 — the type keeps it from being written. */
export type ProfilePatch = Partial<Pick<Profile,
  | 'display_name' | 'home_lat' | 'home_lng' | 'home_districts' | 'near_km' | 'transit_lines'
  | 'english_only' | 'talks_language' | 'home_iata' | 'onboarded_at' | 'calendar_ics_url' | 'home_region'
>> & { second_home?: SecondHome | null }

/** What the hook returns until the row has loaded — and for a user the
 *  server has no row for. Never has a home. */
export const DEFAULT_PROFILE: Profile = {
  user_id: '',
  display_name: null,
  home_lat: null,
  home_lng: null,
  home_districts: [],
  near_km: 2.5,
  transit_lines: [],
  english_only: true,
  talks_language: 'en',
  home_iata: 'BER',
  second_home: null,
  ask_enabled: true,
  model_scoring: false,
  onboarded_at: null,
  calendar_ics_url: null,
  calendar_synced_at: null,
  calendar_sync_note: null,
  // Live since 6 Oct from the unmerged sharing branch (admin_actions,
  // profile_sharing): the columns exist in Postgres, so the defaults must
  // too, or a cached row lacks them (CLAUDE.md's stale-column rule). The
  // branch's readers are not on main yet; these are the migration's defaults.
  paused_at: null,
  auto_report: false,
  saves_visible: true,
  cal_token: null,
  // WP C0.2: the region this person lives in. A profile row cached before the
  // column falls back to it (mergeProfile). C2.2 reads it (`homeRegionOf`,
  // `useCurrentRegion`) and a patch may write it (`ProfilePatch`; the column
  // is granted to `authenticated`, C0.2) - no screen offers it yet.
  home_region: 'berlin',
  created_at: '',
  updated_at: '',
}

/**
 * A fetched row laid over the defaults. A row cached before a column existed
 * lacks it, and `undefined` must not reach a reader (CLAUDE.md: a column
 * younger than the cache) — so every key falls back, and a `null` the server
 * did send (no home) stays null, because that is an answer.
 */
export function mergeProfile(row: Partial<Profile> | null | undefined): Profile {
  if (!row) return DEFAULT_PROFILE
  const out: Record<string, unknown> = { ...DEFAULT_PROFILE }
  for (const [k, v] of Object.entries(row)) if (v !== undefined) out[k] = v
  return out as Profile
}

/** The home point, or null when either half is missing — one half is no
 *  point. Fresh object each call: a caller that wants stability memoises on
 *  the two numbers. */
export function homePoint(p: Pick<Profile, 'home_lat' | 'home_lng'>): { lat: number; lng: number } | null {
  return p.home_lat != null && p.home_lng != null ? { lat: p.home_lat, lng: p.home_lng } : null
}

/** `second_home` read defensively: `second_home` is jsonb, so its shape is
 *  whatever was written, and it is a column younger than the cache. */
export function secondHomeOf(p: Pick<Profile, 'second_home'>): SecondHome | null {
  const raw = p.second_home as Partial<SecondHome> | null | undefined
  if (!raw || typeof raw !== 'object') return null
  return {
    label: typeof raw.label === 'string' ? raw.label : '',
    country: typeof raw.country === 'string' ? raw.country : '',
    airports: Array.isArray(raw.airports) ? raw.airports.filter((a): a is string => typeof a === 'string') : [],
  }
}

/** What the second home is called ('Ireland'), or null when the profile has
 *  none — or has one with no name, which draws nothing a person could read.
 *  Every screen that used to print "Ireland" prints this, and draws nothing
 *  when it is null. */
export function secondHomeLabel(p: Pick<Profile, 'second_home'>): string | null {
  return secondHomeOf(p)?.label.trim() || null
}

/** The second home's country (ISO alpha-2, upper-case), or null: the key
 *  into `holidaysFor` and the flag on its periods. */
export function secondHomeCountry(p: Pick<Profile, 'second_home'>): string | null {
  return secondHomeOf(p)?.country.trim().toUpperCase() || null
}

/** The airports that count as the second home, `[]` for none. The `?? []`
 *  CLAUDE.md asks for, in one place. */
export const secondHomeAirports = (p: Pick<Profile, 'second_home'>): string[] =>
  secondHomeOf(p)?.airports ?? []
